import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDownIcon, EyeIcon } from "@heroicons/react/20/solid";

import useWilmaCustomers from "../../../data/useWilmaCustomers";
import useImpersonation from "../../../hooks/useImpersonation";
import { ICustomerName } from "../../../models/ICustomer";
import { wilmaSAVCustomerId } from "../../../config";
import classNames from "../../../utils/classNames";

/** SAV only: pick a customer to view (and edit) the whole dashboard as. */
export default function CustomerSwitcher({ textColour }: { textColour?: string }) {
  const { isSavUser, impersonating, impersonate, stopImpersonating } = useImpersonation();
  const { customers } = useWilmaCustomers();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const list = useMemo(() => {
    const term = search.trim().toLowerCase();

    return (customers.data?.data ?? [])
      .filter((c) => c.id !== wilmaSAVCustomerId)
      .filter((c) => !term || c.name?.toLowerCase().includes(term))
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [customers.data, search]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setSearch("");
  }, [open]);

  if (!isSavUser) return null;

  const select = (customer: ICustomerName | null) => {
    setOpen(false);
    if ((customer?.id ?? null) === (impersonating?.id ?? null)) return;
    customer ? impersonate(customer) : stopImpersonating();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={classNames(
          "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium ring-1",
          impersonating
            ? "bg-black/10 ring-black/20 hover:bg-black/20"
            : "bg-white text-gray-700 ring-gray-300 hover:bg-gray-50"
        )}
        style={impersonating ? { color: textColour } : undefined}
      >
        <EyeIcon className="h-4 w-4 opacity-70" />
        <span className={classNames("truncate", impersonating ? "hidden md:inline" : "")}>
          {impersonating ? "Switch customer" : "View as customer"}
        </span>
        <ChevronDownIcon className="h-4 w-4 opacity-70" />
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-lg z-50">
          <div className="p-2 border-b border-gray-100">
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers…"
              className="block w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div className="p-2 max-h-80 overflow-auto">
            {impersonating && (
              <button
                type="button"
                onClick={() => select(null)}
                className="w-full rounded-md px-3 py-2 mb-1 text-left text-sm font-medium text-primary-700 hover:bg-gray-50"
              >
                ← Back to SAV
              </button>
            )}

            {customers.isLoading && <div className="p-2 text-sm text-gray-500">Loading customers…</div>}

            {!customers.isLoading && list.length === 0 && (
              <div className="p-2 text-sm text-gray-500">No customers found</div>
            )}

            {list.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => select(c)}
                className="w-full flex items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: c.primaryColour || "#9CA3AF" }}
                  />
                  <span className="truncate">{c.name}</span>
                </div>

                {impersonating?.id === c.id && (
                  <span className="text-xs font-medium text-primary-700">Viewing</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
